import { TrendingDown, TrendingUp } from "lucide-react"
import CountUp from "react-countup"
import { useTranslation } from "react-i18next"

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"

interface MetricData {
  current: number
  previous?: number
  isLoading: boolean
}

interface HeroMetricsProps {
  revenue: MetricData
  volume: MetricData
  averageTicket: MetricData
  approvalRate: MetricData
}

const MetricCard = ({
  title,
  value,
  previousValue,
  prefix = "",
  suffix = "",
  decimals = 0,
  isLoading,
  icon: Icon,
}: {
  title: string
  value: number
  previousValue?: number
  prefix?: string
  suffix?: string
  decimals?: number
  isLoading: boolean
  icon: React.ElementType
}) => {
  const { t } = useTranslation()
  const percentChange =
    previousValue && previousValue > 0
      ? ((value - previousValue) / previousValue) * 100
      : 0
  const hasChange =
    previousValue !== undefined && previousValue > 0
  const isPositive = percentChange > 0
  const hasData =
    value !== null && value !== undefined && value > 0

  return (
    <Card className="group relative overflow-hidden border-t-4 border-t-primary transition-all duration-300 hover:shadow-lg">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
      <CardHeader className="relative pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {title}
          </CardTitle>
          <div className="rounded-full bg-primary/10 p-2">
            <Icon className="h-4 w-4 text-primary" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="relative">
        {isLoading ? (
          <Skeleton className="h-10 w-32" />
        ) : !hasData ? (
          <div className="text-sm text-muted-foreground">
            {t("pages.dashboard.noDataAvailable")}
          </div>
        ) : (
          <>
            <div className="flex items-baseline gap-2">
              <CountUp
                start={0}
                end={value}
                separator="."
                decimals={decimals}
                decimal=","
                prefix={prefix}
                suffix={suffix}
                className="text-3xl font-bold"
              />
            </div>
            {hasChange && (
              <div className="mt-2 flex items-center gap-1 text-sm">
                <div
                  className={cn(
                    "flex items-center gap-1 font-medium",
                    isPositive
                      ? "text-green-600"
                      : "text-red-600"
                  )}
                >
                  {isPositive ? (
                    <TrendingUp className="h-3 w-3" />
                  ) : (
                    <TrendingDown className="h-3 w-3" />
                  )}
                  <span>
                    {Math.abs(percentChange).toFixed(1)}%
                  </span>
                </div>
                <span className="text-muted-foreground">
                  vs período anterior
                </span>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

export const HeroMetrics: React.FC<HeroMetricsProps> = ({
  revenue,
  volume,
  averageTicket,
  approvalRate,
}) => {
  const { t } = useTranslation()

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">
          {t(
            "pages.dashboard.sections.heroMetrics.title",
            "Visão Executiva"
          )}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t(
            "pages.dashboard.sections.heroMetrics.description",
            "Principais indicadores de performance"
          )}
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          title={t(
            "pages.dashboard.cards.transactedAmount.title"
          )}
          value={revenue.current}
          previousValue={revenue.previous}
          prefix="R$ "
          decimals={2}
          isLoading={revenue.isLoading}
          icon={TrendingUp}
        />
        <MetricCard
          title={t(
            "pages.dashboard.cards.totalTransactions.title"
          )}
          value={volume.current}
          previousValue={volume.previous}
          isLoading={volume.isLoading}
          icon={TrendingUp}
        />
        <MetricCard
          title={t(
            "pages.dashboard.cards.approvalIndex.title"
          )}
          value={approvalRate.current}
          suffix="%"
          decimals={1}
          isLoading={approvalRate.isLoading}
          icon={TrendingUp}
        />
        <MetricCard
          title={t(
            "pages.dashboard.cards.averageTicket.title"
          )}
          value={averageTicket.current}
          previousValue={averageTicket.previous}
          prefix="R$ "
          decimals={2}
          isLoading={averageTicket.isLoading}
          icon={TrendingUp}
        />
      </div>
    </div>
  )
}
